/**
 * CSRF Token Service
 * Handles generation, caching, and validation of CSRF tokens
 * to prevent Cross-Site Request Forgery attacks
 */

import { AuthService } from './authService';

export interface CSRFTokenResponse {
  csrf_token: string;
  expires_in: number;
}

class CSRFService {
  private token: string | null = null;
  private tokenExpiry: number | null = null;
  private readonly CSRF_ENDPOINT = `${import.meta.env.VITE_AIRTABLE_PROXY_URL}/csrf-token`;

  /**
   * Get a valid CSRF token, refreshing if necessary
   */
  async getToken(): Promise<string> {
    // Check if we have a valid token that hasn't expired
    if (this.token && this.tokenExpiry && Date.now() < this.tokenExpiry) {
      return this.token;
    }

    // Token is missing or expired, refresh it
    await this.refreshToken();
    
    if (!this.token) {
      throw new Error('Failed to obtain CSRF token');
    }

    return this.token;
  }

  /**
   * Force refresh the CSRF token from the server
   */
  async refreshToken(): Promise<void> {
    try {
      const accessToken = AuthService.getInstance().getAccessToken();
      if (!accessToken) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(this.CSRF_ENDPOINT, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin' // Include cookies for enhanced security
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`CSRF token request failed: ${response.status} ${errorData.message || response.statusText}`);
      }

      const data: CSRFTokenResponse = await response.json();
      
      if (!data.csrf_token) {
        throw new Error('Invalid CSRF token response from server');
      }

      this.token = data.csrf_token;
      // Set expiry to 90% of server-provided expiry to ensure we refresh before expiration
      this.tokenExpiry = Date.now() + (data.expires_in * 1000 * 0.9);

      console.debug('CSRF token refreshed successfully');
      
    } catch (error) {
      console.error('Failed to refresh CSRF token:', error);
      this.clearToken();
      throw error;
    }
  }

  /**
   * Clear the cached token (e.g., on logout or authentication error)
   */
  clearToken(): void {
    this.token = null;
    this.tokenExpiry = null;
  }

  /**
   * Check if we have a valid token without making a network request
   */
  hasValidToken(): boolean {
    return !!(this.token && this.tokenExpiry && Date.now() < this.tokenExpiry);
  }

  /**
   * Get token headers for API requests
   */
  async getTokenHeaders(): Promise<{ 'X-CSRF-Token': string }> {
    const token = await this.getToken();
    return {
      'X-CSRF-Token': token
    };
  }

  /**
   * Make a secure API call with automatic CSRF token handling
   */
  async secureApiCall(url: string, options: RequestInit): Promise<Response> {
    const authToken = AuthService.getInstance().getAccessToken();
    if (!authToken) {
      throw new Error('No authentication token available for secure API call');
    }

    // For state-changing methods, ensure CSRF token is included
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method?.toUpperCase() || '')) {
      const csrfHeaders = await this.getTokenHeaders();
      options.headers = {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
        ...csrfHeaders,
        ...options.headers as Record<string, string>
      };
    } else {
      // For GET requests, just include auth
      options.headers = {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
        ...options.headers as Record<string, string>
      };
    }

    const response = await fetch(url, options);
    
    // If we get a CSRF error, clear the token and retry once
    if (response.status === 403) {
      const errorData = await response.json().catch(() => ({}));
      if (errorData.error?.includes('CSRF') || errorData.error?.includes('csrf')) {
        console.log('CSRF token invalid, clearing cache and retrying...');
        this.clearToken();
        
        // Retry the request with a fresh token for state-changing methods
        if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method?.toUpperCase() || '')) {
          const csrfHeaders = await this.getTokenHeaders();
          options.headers = {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
            ...csrfHeaders,
            ...options.headers as Record<string, string>
          };
        }
        
        return fetch(url, options);
      }
    }
    
    return response;
  }

  /**
   * Handle authentication changes (clear token on logout)
   */
  onAuthenticationChange(isAuthenticated: boolean): void {
    if (!isAuthenticated) {
      this.clearToken();
    }
  }
}

// Export singleton instance
export const csrfService = new CSRFService();

// Listen for authentication changes to clear tokens
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    // Don't clear tokens on page refresh, only on actual logout
  });
}